import { formNaming } from '../components/forms/NewCommunicationForm/constants';
import { decompressSessionStorage } from './sessionStorage';

/**
 * Contexto de navegación de una comunicación (`communication-navigation-context`).
 *
 * Desde el 2026-11-02 las puertas de EDITAR (el lápiz de la tarjeta
 * `CommunicationOT` y el «Editar» de `CommunicationsPage`) escriben `entityId`
 * = la Order de la comunicación que abren. Al llegar a la edición se comprueba
 * como un borrador más (spec wizard-draft-ownership § 6): un contexto de OTRA
 * comunicación (o sin `entityId`) se IGNORA — no se borra, porque el detalle
 * lee la misma clave para su «Volver» — y el destino de vuelta sale del padre
 * que da el servidor.
 */
export type CommunicationNavigationContext = {
  entityId?: string;
  origin?: string;
  returnTo?: string;
  parentOrderId?: string;
  parentRoid?: string | string[];
  parentOrderType?: string;
  entityRoid?: string;
  [key: string]: unknown;
};

type ReadableStore = Pick<Storage, 'getItem'>;

/** El contexto guardado, o `null` si no hay o no se puede leer. Nunca lanza. */
export const readCommunicationNavigationContext = (
  store: ReadableStore = sessionStorage
): CommunicationNavigationContext | null => {
  let raw: string | null;
  try {
    raw = store.getItem(formNaming.COMMUNICATION_NAVIGATION_CONTEXT);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decompressSessionStorage(raw) ?? 'null');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as CommunicationNavigationContext)
      : null;
  } catch {
    return null;
  }
};

/** ¿Es este contexto de la comunicación `entityId`? Solo si lo dice su `entityId`. */
export const communicationContextBelongsTo = (
  context: CommunicationNavigationContext | null,
  entityId: string | undefined
): context is CommunicationNavigationContext =>
  !!context && !!entityId && context.entityId === entityId;

/** El contexto SOLO si es de la comunicación de la URL; si no, `null` (se ignora, no se borra). */
export const readOwnCommunicationNavigationContext = (
  urlEntityId: string | undefined,
  store: ReadableStore = sessionStorage
): CommunicationNavigationContext | null => {
  const context = readCommunicationNavigationContext(store);
  return communicationContextBelongsTo(context, urlEntityId) ? context : null;
};

/**
 * La copia del formulario de la edición, ya parseada, SOLO si es de la Order
 * `orderId`. La copia guarda los valores del formulario, entre ellos `id`.
 * Ilegible, sin id o de otra Order → `null` y el loader carga del servidor.
 * Tras `claimDraftScope` ya es de la URL; esto es el cinturón.
 */
export const readOwnCommunicationEditCopy = (
  orderId: string | undefined,
  store: ReadableStore = sessionStorage
): Record<string, unknown> | null => {
  if (!orderId) return null;
  let raw: string | null;
  try {
    raw = store.getItem(formNaming.EDIT_COMMUNICATION_FORM);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const copy = JSON.parse(decompressSessionStorage(raw) ?? 'null');
    if (!copy || typeof copy !== 'object' || Array.isArray(copy)) return null;
    // Estricto: una copia sin `id` no dice de quién es (una anterior a este
    // cambio no tiene dueño y el reclamo ya la descarta).
    return copy.id === orderId ? copy : null;
  } catch {
    return null;
  }
};
